import { checkIsOrderContainNonTradeInItems, checkIsTradeInPurchase } from "@jti/commerce/shared";
import type { Order } from "@jti/magento";
import type { MagentoShippingMethod } from "@jti/magento-types";
import { getSessionStorage } from "@jti/rest-api";
import type { CartSliceState } from "@jti/store/slices/cartSlice";
import type { FreeTrialCart } from "@jti/store/types";
import type { CheckoutTemplateName, TemplateName, TrackingEventsAdditionalData } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { CheckOutSteps } from "@checkout/constants/checkout-constants";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import firstLetterCapitalize from "@checkout/utils/firstLetterCapitalize";

import { buildCommerceBaseData, buildPurchaseData, buildTradeInPurchaseData } from "./trackingHelpers";

export type UseGtmType = {
  dispatchGtmCheckoutProgress: (trackingData: CartSliceState | FreeTrialCart | Order, step?: string) => void;
  dispatchGtmCheckoutBegin: (cart: CartSliceState | FreeTrialCart | Order) => void;
  dispatchGtmAddPaymentInfo: (data: CartSliceState | FreeTrialCart | Order | undefined | null, code: string) => void;
  dispatchGtmOrderSummaryPurchase: (order: Order | undefined) => void;
  dispatchGtmShippingInfo: (cart: CartSliceState | FreeTrialCart, method: MagentoShippingMethod) => void;
  dispatchGtmPageViewEvent: (template?: TemplateName) => void;
  updateGtmPageName: (template: string) => Promise<void>;
  dispatchCartView: (cart: CartSliceState | FreeTrialCart | Order) => void;
};

export type SharedData = {
  section: TemplateName;
  template_name: TemplateName;
  checkout_type: TemplateName;
};

export const getTemplateName = (pathname: TemplateName): SharedData => {
  return {
    section: pathname,
    template_name: pathname,
    checkout_type: pathname,
  };
};

export const useGtm = (): UseGtmType => {
  const { isFreeTrial } = useFreeTrial();
  const templateName: CheckoutTemplateName = isFreeTrial ? "free-trial-checkout" : "checkout";
  const sharedData = getTemplateName(templateName);

  const promiseTimeoutInMs = 15000;

  const dispatchGtmPageViewEvent: UseGtmType["dispatchGtmPageViewEvent"] = (template = templateName) => {
    dispatchTrackingEvent(TrackingEvents.TrackingCheckoutPageView, {
      section: template,
      template_name: template,
      checkout_type: templateName as TemplateName,
    });
  };

  const dispatchGtmCheckoutBegin: UseGtmType["dispatchGtmCheckoutBegin"] = (trackingData?) => {
    if (!trackingData) {
      return;
    }

    const data: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutBeginCheckout] = {
      section: sharedData.section,
      template_name: sharedData.template_name,
      ...buildCommerceBaseData(trackingData),
    };
    dispatchTrackingEvent(TrackingEvents.TrackingCheckoutBeginCheckout, data);
  };

  const dispatchGtmCheckoutProgress: UseGtmType["dispatchGtmCheckoutProgress"] = (trackingData, step) => {
    if (!step || !trackingData) {
      return;
    }
    const stepIndex = Object.keys(CheckOutSteps).indexOf(firstLetterCapitalize(step));

    if (!stepIndex) {
      dispatchGtmCheckoutBegin(trackingData);
    }

    const checkoutProgressData: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutProgress] = {
      ...sharedData,
      ...buildCommerceBaseData(trackingData),
      checkout_step_name: step,
      checkout_step_number: stepIndex + 1,
    };
    dispatchTrackingEvent(TrackingEvents.TrackingCheckoutProgress, checkoutProgressData);
  };

  const dispatchGtmAddPaymentInfo: UseGtmType["dispatchGtmAddPaymentInfo"] = (cart, code): void => {
    if (!cart || !code) {
      return;
    }

    const checkoutAddPaymentInfoData: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutAddPaymentInfo] = {
      ...sharedData,
      ...buildCommerceBaseData(cart),
      payment_type: code,
    };
    dispatchTrackingEvent(TrackingEvents.TrackingCheckoutAddPaymentInfo, checkoutAddPaymentInfoData);
  };

  const dispatchGtmShippingInfo: UseGtmType["dispatchGtmShippingInfo"] = (cart, method) => {
    if (!cart) {
      return;
    }
    const checkoutAddShippingInfoData: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutAddShippingInfo] = {
      section: sharedData.section,
      template_name: sharedData.template_name,
      ...buildCommerceBaseData(cart),
      shipping_tier: method.carrierCode,
    };
    dispatchTrackingEvent(TrackingEvents.TrackingCheckoutAddShippingInfo, checkoutAddShippingInfoData);
  };

  const dispatchGtmOrderSummaryPurchase = (order?: Order): void => {
    if (!order) {
      return;
    }

    const orderConfirmationTemplate: TemplateName = isFreeTrial
      ? "free-trial-order-confirmation-page"
      : "order-confirmation";

    dispatchGtmPageViewEvent(orderConfirmationTemplate);

    if (checkIsTradeInPurchase(order)) {
      const purchaseData = buildTradeInPurchaseData({ order, sharedData, isTradeInPurchase: true });
      dispatchTrackingEvent(TrackingEvents.TrackingOrderTradeIn, purchaseData);
    }

    if (checkIsOrderContainNonTradeInItems(order)) {
      const purchaseData = buildPurchaseData({ order, sharedData });
      dispatchTrackingEvent(TrackingEvents.TrackingOrderSummaryPurchase, purchaseData);
    }
  };

  const updateGtmPageName: UseGtmType["updateGtmPageName"] = async (template) => {
    const gtmContainer = document.getElementById("gtmContainer");
    if (gtmContainer) {
      await new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          if (getSessionStorage().get("gtmInstance", "")) {
            resolve(template);
          }
        }, 500);

        setTimeout(() => {
          reject(new Error("GTM instance is unavailable"));
          clearInterval(interval);
        }, promiseTimeoutInMs);
      });
      gtmContainer.dataset.pageName = template;
    }
  };

  const dispatchCartView: UseGtmType["dispatchCartView"] = (cart) => {
    const data: TrackingEventsAdditionalData[TrackingEvents.TrackingCartOpen] = buildCommerceBaseData(cart);
    dispatchTrackingEvent(TrackingEvents.TrackingCartOpen, data);
  };

  return {
    dispatchGtmCheckoutProgress,
    dispatchGtmCheckoutBegin,
    dispatchGtmAddPaymentInfo,
    dispatchGtmOrderSummaryPurchase,
    dispatchGtmShippingInfo,
    dispatchGtmPageViewEvent,
    updateGtmPageName,
    dispatchCartView,
  };
};
